import React, { useState } from 'react';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import { useNCZIContext } from '../../../Services/NCZIContext';
import { useEffectAsync } from '../../../../../helpers';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import EHRResult from '../../EHR/EHRResult/EHRResult';
import EHRExtractUnpackCompositionItems from '../../EHR/EHRExtract/EHRExtractUnpackCompositionItems';
import { Theme } from '../../../../../Enums/Theme.enum';
import LinkButton from '../../../../../Components/Button/LinkButton';
import ElectronicExaminationCreateModal from '../Create/ElectronicExaminationCreateModal';
import { PatientConsentType } from '../../../Commands/WritePatientConsent';
import ElectronicExaminationFilter from '../Filter/ElectronicExaminationFilter';
import PatientConsentButton from '../../PatientConsentModal/PatientConsentButton';
import useNCZIElectronicExamination from '../../../Hooks/useNCZIElectronicExamination';
import NCZIListPagination from '../../NCZIListPagination/NCZIListPagination';

//@ts-ignore
import styles from './ElectronicExaminationList.module.scss';
import { TYPE_ZZS } from '../Create/ElectronicExaminationCreateForm';

interface Props {
	setIsLoading: (isLoading: boolean) => void;
}

const ElectronicExaminationList: React.FC<Props> = () => {
	const {
		patientExaminationData,
		patientExaminationPage,
		patientIdentifier,
		patientExaminationFilter
	} = useNCZIContext();

	const {
		handleLoadPatientExaminationData,
		handleFakeLoadPatientExaminationData,
		loadingCommandId
	} = useNCZIElectronicExamination();

	const [electronicExaminationCreateOpen, setElectronicExaminationCreateOpen] =
		useState<boolean>(false);
	const [electronicExaminationCreateHealthAssessmentOpen, setElectronicExaminationCreateHealthAssessmentOpen] =
		useState<boolean>(false);

	const handleLoadData = async () => {
		if (!patientExaminationData) {
			await handleLoadPatientExaminationData(1, patientExaminationFilter);
			// await handleFakeLoadPatientExaminationData();
		}
	};

	useEffectAsync(async () => {
		await handleLoadData();
	}, []);

	const handleOpenCreateRecord = () => {
		setElectronicExaminationCreateOpen(true);
	};

	const handleOpenCreateHealthAssessmentRecord = () => {
		setElectronicExaminationCreateHealthAssessmentOpen(true);
	};

	const handleCloseElectronicExaminationCreate = async (
		needReload: boolean,
	) => {
		setElectronicExaminationCreateOpen(false);
		setElectronicExaminationCreateHealthAssessmentOpen(false);
		if (needReload) {
			await handleLoadPatientExaminationData(1);
		}
	};

	const handleCloseDetailModal = async (needReload: boolean) => {
		if (needReload) {
			await handleLoadPatientExaminationData(patientExaminationPage);
		}
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.filterWrapper}>
				<ElectronicExaminationFilter onFilter={(values) => handleLoadPatientExaminationData(1, values)} />
			</div>
			<div className={styles.contentWrapper}>
				<div className={styles.controls}>
					<div className={styles.subcontrols}>
						<LinkButton
							title='nczi.examination.create.titleZoOV'
							theme={Theme.BLUE}
							onClick={handleOpenCreateRecord}
							firstUpper
						/>
						<LinkButton
							title='nczi.examination.create.titleZZS'
							theme={Theme.BLUE}
							onClick={handleOpenCreateHealthAssessmentRecord}
							firstUpper
						/>
					</div>
					<PatientConsentButton
						loadData={() => handleLoadPatientExaminationData(1, patientExaminationFilter)}
						consentType={PatientConsentType.VVP}
						code={patientExaminationData?.result?.code}
					/>
				</div>
				<ScrollContainer>
					<div className={styles.content}>
						{!patientExaminationData || !patientIdentifier ? (
							<div className={styles.loadingWrapper}>
								<NCZICommandResult
									commandId={loadingCommandId}
									handleRetryCommand={() => handleLoadPatientExaminationData(patientExaminationPage, patientExaminationFilter)}
								/>
							</div>
						) : (
							<>
								<EHRResult ehrResult={patientExaminationData?.result} />
								<EHRExtractUnpackCompositionItems
									ehrExtract={patientExaminationData?.ehrExtract}
									patientIdentifier={patientIdentifier}
									onCloseDetail={handleCloseDetailModal}
								/>
								<NCZIListPagination
									hasMoreData={
										(patientExaminationData?.ehrExtract?.items[0]?.items || []).length > 0
									}
									page={patientExaminationPage ?? 1}
									loadData={(page) => handleLoadPatientExaminationData(page, patientExaminationFilter)}
								/>
							</>
						)}
						<ElectronicExaminationCreateModal
							opened={electronicExaminationCreateOpen}
							onClose={handleCloseElectronicExaminationCreate}
							patientIdentifier={patientIdentifier || ''}
						/>
						<ElectronicExaminationCreateModal
							opened={electronicExaminationCreateHealthAssessmentOpen}
							onClose={handleCloseElectronicExaminationCreate}
							type={TYPE_ZZS}
							patientIdentifier={patientIdentifier || ''}
						/>
					</div>
				</ScrollContainer>
			</div>
		</div>
	);
};

export default ElectronicExaminationList;
